import { PaginationState } from '@tanstack/react-table';
import { DatePickerRange } from '../../components';
import {
  ApiSuccess,
  ApiTagTypes,
  Call,
  CallKind,
  CustomSortingState,
  FlaggedUser,
  PagePagination,
  ProspectCategory,
  RealCall,
  ReviewFilterKeys,
  SimulationCallWithApiListPracticeProspect,
} from '../../types';
import { getUrlWithPaginationAndSorting, parseProspect } from '../../utils';
import { apiSlice } from '../api.services';

// TODO: Extend the ReviewFilters type when the API accepts the date as a single value.
interface GetCallsPayload {
  // Filters
  [ReviewFilterKeys.CATEGORY]?: ProspectCategory[];
  [ReviewFilterKeys.INCOMING_PHONE_NUMBER]?: string[];
  [ReviewFilterKeys.PROSPECT]?: string[];
  [ReviewFilterKeys.CALL_DATE]?: DatePickerRange[];
  [ReviewFilterKeys.TAGS]?: string[];
  [ReviewFilterKeys.USER]?: string[];
  [ReviewFilterKeys.FLAGGED]?: boolean;
  [ReviewFilterKeys.TAGS]?: string[];
  [ReviewFilterKeys.TEAM]?: string[];
  // Controls
  pagination?: PaginationState;
  sorting?: CustomSortingState;
  // Call kind
  callKind?: CallKind;
}

type FlagCallPayload = {
  callSid: string;
  flaggedUsers: FlaggedUser[];
};

type SendCallFeedbackPayload = {
  callSid: string;
  isSatisfied: boolean;
};

type GetCallsResponse = {
  data: {
    calls: SimulationCallWithApiListPracticeProspect[] | RealCall[];
    pagination: PagePagination;
  };
};

const enhancedApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCalls: builder.mutation<{ calls: Call[]; pagination: PagePagination }, GetCallsPayload>({
      query: ({ pagination, sorting, ...filters }) => ({
        url: getUrlWithPaginationAndSorting('/calls', pagination, sorting),
        method: 'POST',
        body: filters,
      }),

      transformResponse: (response: GetCallsResponse) => ({
        calls: response.data.calls.map((call) => {
          if (call.callKind === CallKind.SIMULATION) {
            return {
              ...call,
              practiceProspect: parseProspect(call.practiceProspect),
            };
          }
          return call;
        }),
        pagination: response.data.pagination,
      }),
    }),

    flagCall: builder.mutation<ApiSuccess, FlagCallPayload>({
      query: ({ callSid, flaggedUsers }) => ({
        url: `/calls/${callSid}/flag`,
        method: 'POST',
        body: { flaggedUsers },
      }),
      invalidatesTags: (_, __, { callSid }) => [{ type: ApiTagTypes.CALLS, id: callSid }],
    }),
    unflagCall: builder.mutation<ApiSuccess, string>({
      query: (callSid) => ({
        url: `/calls/${callSid}/flag`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, callSid) => [{ type: ApiTagTypes.CALLS, id: callSid }],
    }),
    generateCallCoaching: builder.mutation<ApiSuccess, string>({
      query: (callSid) => ({
        url: `/calls/${callSid}/ai-coaching`,
        method: 'POST',
      }),
    }),
    sendCallFeedback: builder.mutation<ApiSuccess, SendCallFeedbackPayload>({
      query: ({ callSid, ...payload }) => ({
        url: `/calls/${callSid}/feedback`,
        method: 'POST',
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetCallsMutation,
  useFlagCallMutation,
  useUnflagCallMutation,
  useGenerateCallCoachingMutation,
  useSendCallFeedbackMutation,
} = enhancedApiSlice;
